import { useCurrentAccount, useCurrentUser } from '@teamwork/use';
import * as LDClient from 'launchdarkly-js-client-sdk';
import { inject, onScopeDispose, provide, shallowRef, watch } from 'vue-demi';

const clientIds = {
  local: '610bbe8b8509bc25794c51b0',
  staging: '6148798178b2ec266dd4a3f5',
  prod: '610bbe8b8509bc25794c51b1',
};

function getClientKey(account) {
  if (account.isStaging) {
    return clientIds.staging;
  }
  if (window.devMode && account.URL === 'https://sunbeam.teamwork.localhost/') {
    return clientIds.local;
  }
  return clientIds.prod;
}

function createClient(account, user) {
  const clientId = getClientKey(account);

  const ldUser = {
    key: `${account.id}-${user.id}`,
    firstName: user.firstName,
    lastName: user.lastName,
    email: user.emailAddress,
    custom: {
      region: account.awsRegion,
      shardNo: account.shardNo,
      projectsPlanId: account.pricePlanId,
      installationId: account.id,
      installationIsTestAccount: account.installationIsTestAccount,
      industryCategoryId: parseInt(account.industryCategoryId, 10),
      projectsStartDate: account.dateSignedUp.clone().utc().format(),
      projectsIsPaid: account.isPaid,
      projectsPaymentStatus: account.paymentStatus,
      projectsTrialDaysRemaining: parseInt(account.trialDaysRemaining, 10),
      projectsPaymentMethod: account.paymentMethod,
      projectsInOwnerCompany: user.inOwnerCompany,
      projectsSiteOwner: user.siteOwner,
      projectAdministrator: user.administrator,
      projectsIsClientUser: user.isClientUser,
      projectsIsCollaborator: user.isCollaborator,
      projectsCompanyRoleId: user.companyRoleId,
    },
  };

  const options = {
    hash: user.ldHash,
    bootstrap: 'localStorage',
    evaluationReasons: true,
  };

  return LDClient.initialize(clientId, ldUser, options);
}

const launchDarklySymbol = Symbol('useLaunchDarkly');

/**
 * Initializes a LaunchDarkly client once useCurrentAccount and useCurrentUser are initialized.
 * It currently does not reinitialize when the current account and current user change.
 */
export function provideLaunchDarkly() {
  const account = useCurrentAccount();
  const user = useCurrentUser();
  const client = shallowRef();
  const ready = shallowRef(false);
  const initialized = shallowRef(false);

  const ldTrackCustomEvent = (key, data, metricValue) => {
    if (ready.value) {
      client.value.track(key, data, metricValue);
    }
  };

  watch(
    [account, user],
    () => {
      if (!client.value && account.value && user.value) {
        client.value = createClient(account.value, user.value);
        client.value.on('ready', () => {
          ready.value = true;
        });
        client.value.on('initialized', () => {
          initialized.value = true;
        });
      }
    },
    { immediate: true },
  );

  onScopeDispose(() => {
    if (client.value) {
      client.value.close();
    }
  });

  provide(launchDarklySymbol, {
    client,
    ready,
    initialized,
    ldTrackCustomEvent,
  });
}

export function useLaunchDarkly() {
  return inject(launchDarklySymbol);
}
